import { FC, useMemo, useState, useRef } from 'react';
import { Box, IconButton, Modal, Typography, Chip, Popover } from '@mui/material';
import { styled } from '@mui/material/styles';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import CloseFullscreenOutlinedIcon from '@mui/icons-material/CloseFullscreenOutlined';
import RichTextEditor, {
  ToolbarOptions,
} from '../../../common/components/RichTextEditor/RichTextEditor';
import { LinkedSession } from '../../../slices/ideationSlice/newIdeationSlice';
import { useNavigate } from 'react-router-dom';

const ExpandableFieldContainer = styled(Box)(() => ({
  position: 'relative',
  '&:hover .expand-button': {
    opacity: 1,
  },
}));

const ExpandButton = styled(IconButton)(() => ({
  position: 'absolute',
  bottom: 8,
  right: 8,
  zIndex: 5,
  opacity: 0.7,
  transition: 'opacity 0.2s ease-in-out',
  backgroundColor: 'rgba(255, 255, 255, 0.9)',
  backdropFilter: 'blur(4px)',
  border: '1px solid rgba(0, 0, 0, 0.1)',
  width: 28,
  height: 28,
  '&:hover': {
    opacity: 1,
    backgroundColor: 'rgba(255, 255, 255, 1)',
    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.08)',
  },
}));

type OpportunityMultilineFieldProps = {
  label: string;
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  minRows?: number;
  onUseDataXplrClick?: (label: string) => void;
  disabled?: boolean;
  linkedSessions?: LinkedSession[];
  fieldCategory?: string; // Category for navigation state
  onRemoveLinkedSessions?: () => void; // Callback to remove linked sessions and call delete API
};

const OpportunityMultilineField: FC<OpportunityMultilineFieldProps> = ({
  label,
  placeholder,
  value,
  onChange,
  required = false,
  minRows = 6,
  onUseDataXplrClick,
  disabled = false,
  linkedSessions = [],
  fieldCategory,
}) => {
  const [expanded, setExpanded] = useState<boolean>(false);
  const [popoverOpen, setPopoverOpen] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const navigate = useNavigate();
  const popoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const inlineHeight = useMemo(() => `${Math.max(minRows, 6) * 38}px`, [minRows]);
  const inlineToolbarOptions: ToolbarOptions = useMemo(
    () => ({
      fontFamily: false,
      bold: true,
      italic: true,
      underline: true,
      textColor: false,
      highlight: false,
      bulletList: true,
      orderedList: true,
      alignLeft: false,
      alignCenter: false,
      alignRight: false,
      link: false,
      image: false,
      codeBlock: false,
      undo: true,
      redo: true,
      table: false,
    }),
    []
  );

  const handleOpen = () => setExpanded(true);
  const handleClose = () => setExpanded(false);

  const handleChipMouseEnter = (event: React.MouseEvent<HTMLElement>) => {
    // Clear any existing timeout
    if (popoverTimeoutRef.current) {
      clearTimeout(popoverTimeoutRef.current);
      popoverTimeoutRef.current = null;
    }
    setAnchorEl(event.currentTarget);
    setPopoverOpen(true);
  };

  const handleChipMouseLeave = () => {
    // Delay closing the popover to allow mouse to move to popover
    popoverTimeoutRef.current = setTimeout(() => {
      setPopoverOpen(false);
      setAnchorEl(null);
    }, 150);
  };

  const handlePopoverMouseEnter = () => {
    // Keep popover open when mouse enters popover
    if (popoverTimeoutRef.current) {
      clearTimeout(popoverTimeoutRef.current);
      popoverTimeoutRef.current = null;
    }
  };

  const handlePopoverMouseLeave = () => {
    // Close popover when mouse leaves popover
    setPopoverOpen(false);
    setAnchorEl(null);
  };

  const handleSessionClick = (sessionId: string, field_type: string) => {
    setPopoverOpen(false);
    setAnchorEl(null);

    // Get the field label from the fieldCategory for proper category mapping
    const categoryLabels: Record<string, string> = {
      current_process: 'currentProcess',
      expected_impact: 'expectedImpact',
      ideas_of_improvement: 'specificIdeasOfImprovement',
      key_performance_indicator: 'keyPerformanceIndicators',
    };

    const fieldKey = field_type || categoryLabels[fieldCategory || ''] || 'currentProcess';

    navigate(`/data-xplr/${sessionId}?selectedCategory=${fieldKey}&fromIdeation=true`, {
      state: {
        fromDrawer: false,
        selectedCategory: fieldKey,
        deepLink: true,
      },
    });
  };

  const renderDataXplrAction = () => {
    const isActive = onUseDataXplrClick && !disabled;

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 1,
          mt: 2.5,
          borderRadius: 1,
          cursor: isActive ? 'pointer' : 'not-allowed',
          opacity: isActive ? 1 : 0.4,
          pointerEvents: isActive ? 'auto' : 'none',
          '&:hover': {
            backgroundColor: isActive ? '#f5f5f5' : 'transparent',
          },
        }}
        onClick={isActive ? () => onUseDataXplrClick?.(label) : undefined}
      >
        <img src="/images/dataXplrIcon.png" alt="Use Data XPLR" width={16} height={16} />
        <Typography
          variant="body1"
          fontWeight={400}
          color="primary"
          sx={{ fontSize: '14px', ml: 1 }}
        >
          Add from Data XPLR
        </Typography>
      </Box>
    );
  };

  return (
    <>
      <Box sx={{ cursor: disabled ? 'not-allowed' : 'auto' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 1,
            mt: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography
              variant="body1"
              fontWeight={600}
              sx={{ fontSize: '16px', mt: 2.5, color: '#131426' }}
            >
              {label}
              {required ? <span style={{ color: 'red' }}>*</span> : null}
            </Typography>

            {linkedSessions.length > 0 && (
              <Chip
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <img
                      src="/images/linkedObjects.svg"
                      alt="linked"
                      style={{ width: '16px', height: '16px' }}
                    />
                    {linkedSessions.length === 1 ? 'Linked Object' : `Linked Objects`}
                  </Box>
                }
                size="small"
                variant="outlined"
                sx={{
                  fontSize: '14px',
                  fontWeight: 400,
                  color: '#ffffff',
                  borderRadius: '6px',
                  borderColor: '#11953E',
                  mt: 2.5,
                  backgroundColor: '#11953E',
                  '&:hover': {
                    backgroundColor: '#11953E',
                  },
                  cursor: 'pointer',
                }}
                onMouseEnter={handleChipMouseEnter}
                onMouseLeave={handleChipMouseLeave}
              />
            )}
          </Box>

          {renderDataXplrAction()}
        </Box>

        <ExpandableFieldContainer
          sx={{
            cursor: disabled ? 'not-allowed' : 'text',
            '& .ProseMirror': {
              pointerEvents: disabled ? 'none' : undefined,
              cursor: disabled ? 'not-allowed' : 'text',
            },
            '& *': disabled
              ? {
                  cursor: 'not-allowed !important',
                }
              : undefined,
          }}
        >
          <RichTextEditor
            content={value}
            onChange={onChange}
            placeholder={placeholder}
            toolbarOptions={inlineToolbarOptions}
            showToolbarOnFocus={!disabled}
            height={inlineHeight}
            maxHeight={inlineHeight}
            enableComments={false}
            readOnly={disabled}
          />

          <ExpandButton
            className="expand-button"
            size="small"
            onClick={handleOpen}
            disabled={disabled}
            sx={{
              opacity: disabled ? 0.3 : undefined,
              pointerEvents: disabled ? 'none' : 'auto',
              mr: 2,
            }}
          >
            <FullscreenIcon sx={{ fontSize: 16 }} />
          </ExpandButton>
        </ExpandableFieldContainer>
      </Box>

      <Modal open={expanded} onClose={handleClose}>
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.25)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: { xs: 1, sm: 2 },
          }}
        >
          <Box
            sx={{
              width: { xs: '100%', sm: '90%', md: '80%' },
              height: { xs: '100%', sm: '90%', md: '80%' },
              maxHeight: { xs: '100vh', sm: '90vh', md: '80vh' },
              backgroundColor: 'white',
              borderRadius: 2,
              boxShadow: 24,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                px: 3,
                py: 2,
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography variant="h6" fontWeight={600}>
                {label}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {/* {renderDataXplrAction()} */}
                <IconButton onClick={handleClose} size="small">
                  <CloseFullscreenOutlinedIcon />
                </IconButton>
              </Box>
            </Box>

            <Box
              sx={{
                flex: 1,
                p: { xs: 2, sm: 3 },
                overflow: 'hidden',
                minHeight: 0,
              }}
            >
              <RichTextEditor
                key={`${label}-expanded`}
                content={value}
                onChange={onChange}
                placeholder={placeholder}
                toolbarOptions={inlineToolbarOptions}
                showToolbarOnFocus={false}
                enableComments={false}
                readOnly={disabled}
                height="100%"
                maxHeight="100%"
              />
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* Hover Popover for Session Details */}
      <Popover
        open={popoverOpen}
        anchorEl={anchorEl}
        disableRestoreFocus
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        sx={{
          pointerEvents: 'none',
        }}
        PaperProps={{
          sx: {
            pointerEvents: 'auto',
            backgroundColor: '#ffffff',
            border: '1px solid #E0E0E0',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            mt: 0.5,
            minWidth: '300px',
            maxWidth: '400px',
          },
          onMouseEnter: handlePopoverMouseEnter,
          onMouseLeave: handlePopoverMouseLeave,
        }}
      >
        <Box>
          {linkedSessions.map((item: any) => (
            <Box
              key={item.id}
              onClick={() => handleSessionClick(item?.conversation_id, item?.field_type)}
              sx={{
                px: 2,
                py: 1.2,
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: '#FFFFFF',
                  textColor: '#1976d2',
                  color: '#1976d2', // MUI primary link blue
                  textDecoration: 'underline',
                },
              }}
            >
              <Typography
                sx={{
                  fontSize: 14,
                  color: '#212121',
                  fontWeight: 400,
                  '&:hover': {
                    color: '#1976d2',
                  },
                }}
              >
                {item.use_case_title}
              </Typography>
            </Box>
          ))}
        </Box>
      </Popover>
    </>
  );
};

export default OpportunityMultilineField;
